import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineCamera, AiOutlineWarning } from "react-icons/ai";
import { RxCross1 } from "react-icons/rx";
import { toast } from "react-toastify";
import { updateUserInformation, loadUser } from "../../redux/actions/user";
import { backend_url, server } from "../../server";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import axios from "axios";
import Address from "../../components/Profile/Address";
import LinkedSocialProfiles from "../../components/Profile/LinkedSocialProfiles";

const DELETE_CONFIRM_TEXT = "DELETE";

const ProfileOverviewNew = () => {
  const { user, error, successMessage } = useSelector((state) => state.user);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [ukaraNumber, setUkaraNumber] = useState("");
  const [ukaraProofUploading, setUkaraProofUploading] = useState(false);
  const [, setAvatar] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const dispatch = useDispatch();
  const didClickSaveRef = useRef(false);

  useEffect(() => {
    if (!user?._id) {
      return;
    }
    setName(user?.name || "");
    setEmail(user?.email || "");
    setPhoneNumber(user?.phoneNumber || "");
    setUkaraNumber(user?.ukaraNumber || "");
  }, [user?._id, user?.name, user?.email, user?.phoneNumber, user?.ukaraNumber]);

  useEffect(() => {
    if (error) {
      const errLower = error.toLowerCase();
      const isAuthError = errLower.includes("login") ||
                         errLower.includes("authenticate") ||
                         errLower.includes("unauthorized") ||
                         errLower.includes("expired") ||
                         errLower.includes("url is invalid") ||
                         errLower.includes("token");

      if (!isAuthError || !user) {
        toast.error(error);
      }
      dispatch({ type: "clearErrors" });
    }
    if (successMessage) {
      toast.success(successMessage);
      dispatch({ type: "clearMessages" });
    }
  }, [dispatch, error, successMessage, user]);

  const inputClasses =
    "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 placeholder:text-slate-400 transition focus:border-[#38513b] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#38513b]/20";
  const labelClasses = "block text-sm font-medium text-slate-600";
  const primaryButtonClasses =
    "inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232]";
  const secondaryButtonClasses =
    "inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]";
  const cardClass =
    "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8";

  const resetPersonalDetails = () => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setPhoneNumber(user?.phoneNumber || "");
    setPassword("");
    setUkaraNumber(user?.ukaraNumber || "");
  };

  const openDeleteModal = () => {
    setDeletePassword("");
    setDeleteConfirmText("");
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (!deleteLoading) {
      setDeleteModalOpen(false);
      setDeletePassword("");
      setDeleteConfirmText("");
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmText !== DELETE_CONFIRM_TEXT) {
      toast.error(`Please type ${DELETE_CONFIRM_TEXT} to confirm.`);
      return;
    }
    if (!deletePassword.trim()) {
      toast.error("Please enter your password.");
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.post(
        `${server}/user/delete-my-account`,
        { password: deletePassword },
        { withCredentials: true }
      );
      toast.success("Your account has been deleted.");
      setDeleteModalOpen(false);
      setDeletePassword("");
      setDeleteConfirmText("");
      window.location.href = "/login";
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete account.";
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      toast.error(`Image exceeds the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`);
      e.target.value = "";
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setAvatar(reader.result);
        }
      };
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("image", file);

      await axios
        .put(`${server}/user/update-avatar`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        })
        .then((res) => {
          dispatch(loadUser());
          toast.success("Avatar updated successfully!");
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to update avatar");
        });
    }
  };

  const handleUkaraProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      toast.error(`Image exceeds the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`);
      e.target.value = "";
      return;
    }

    try {
      setUkaraProofUploading(true);
      const formData = new FormData();
      formData.append("proof", file);
      await axios.put(`${server}/user/update-ukara-proof`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await dispatch(loadUser());
      toast.success("UKARA proof uploaded successfully.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload UKARA proof.");
    } finally {
      setUkaraProofUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!didClickSaveRef.current) return;
    didClickSaveRef.current = false;
    const success = await dispatch(
      updateUserInformation({
        name,
        email,
        phoneNumber,
        password: String(password || "").trim(),
        ukaraNumber,
      })
    );
    if (success) {
      setPassword("");
    }
  };

  return (
    <div className="space-y-6">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Overview</h2>
        <p className="text-sm text-slate-500">
          Manage your personal information and delivery addresses.
        </p>
      </header>
      {/* Personal Details Section */}
      <section className={cardClass}>
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="flex items-center justify-center lg:w-1/3">
            <div className="relative flex flex-col items-center gap-4">
              <img
                src={`${backend_url}${user?.avatar || "default-avatar.png"}`}
                alt="Profile avatar"
                className="h-40 w-40 rounded-3xl border-4 border-[#38513b]/20 object-contain shadow-md"
                onError={(e) => {
                  e.target.src = `${backend_url}default-avatar.png`;
                }}
              />
              <label
                htmlFor="image"
                className="group inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
              >
                <AiOutlineCamera size={18} />
                <span>Change photo</span>
                <input
                  type="file"
                  id="image"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImage}
                />
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB per image. Recommended size: 200x200px (square image)
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 space-y-6"
            aria-required="true"
          >
            <header>
              <h2 className="text-lg font-semibold text-slate-900">
                Personal details
              </h2>
              <p className="text-sm text-slate-500">
                Update your account information to keep your profile in sync.
              </p>
            </header>

            <div className="grid gap-4 sm:grid-cols-2">
              {user?.username && (
                <label className="flex flex-col gap-2 sm:col-span-2">
                  <span className={labelClasses}>Username</span>
                  <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                    {user.username}
                  </p>
                  <span className="text-xs text-slate-500">
                    This is your public display name on listings and in messages.
                  </span>
                </label>
              )}
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Full name</span>
                <input
                  type="text"
                  className={inputClasses}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Email address</span>
                <input
                  type="email"
                  className={inputClasses}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Phone number</span>
                <input
                  type="tel"
                  className={inputClasses}
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>UKARA number (optional)</span>
                <input
                  type="text"
                  className={inputClasses}
                  value={ukaraNumber}
                  onChange={(e) => setUkaraNumber(e.target.value.replace(/\s+/g, "").toUpperCase())}
                  placeholder="e.g. ABC123456"
                />
              </label>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <span className={labelClasses}>Upload proof of your UKARA membership (optional)</span>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]">
                    {ukaraProofUploading ? "Uploading..." : "Upload proof"}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={handleUkaraProofUpload}
                      disabled={ukaraProofUploading}
                    />
                  </label>
                  {user?.ukaraProofImage ? (
                    <a
                      href={`${backend_url}${user.ukaraProofImage}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      View uploaded proof
                    </a>
                  ) : (
                    <span className="text-sm text-slate-500">No proof uploaded yet</span>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  This proof is visible to sellers when they review your order UKARA details.
                </p>
              </div>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Confirm with password</span>
                <input
                  type="password"
                  name="current-password"
                  autoComplete="current-password"
                  className={inputClasses}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={resetPersonalDetails}
                className={secondaryButtonClasses}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={primaryButtonClasses}
                onMouseDown={() => {
                  didClickSaveRef.current = true;
                }}
              >
                Save changes
              </button>
            </div>
          </form>
        </div>
      </section>

      <LinkedSocialProfiles
        cardClass={cardClass}
        inputClasses={inputClasses}
        labelClasses={labelClasses}
        primaryButtonClasses={primaryButtonClasses}
        secondaryButtonClasses={secondaryButtonClasses}
      />

      {/* Addresses Section */}
      <Address
        cardClass={cardClass}
        inputClasses={inputClasses}
        labelClasses={labelClasses}
        primaryButtonClasses={primaryButtonClasses}
        secondaryButtonClasses={secondaryButtonClasses}
      />

      {/* Danger zone: Delete account */}
      <section className={cardClass}>
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-slate-900">Delete account</h2>
          <p className="text-sm text-slate-500">
            Once you delete your account, there is no going back. All your data will be permanently removed.
          </p>
          <button
            type="button"
            onClick={openDeleteModal}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-6 text-sm font-semibold text-red-600 transition hover:border-red-300 hover:bg-red-50"
          >
            <AiOutlineWarning size={18} />
            Delete my account
          </button>
        </div>
      </section>

      {/* Delete account confirmation modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-900/40 px-4 py-10 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Delete account</h3>
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="rounded-full border border-transparent p-1 text-slate-500 transition hover:border-slate-200 hover:text-slate-700 disabled:opacity-50"
              >
                <RxCross1 size={22} />
              </button>
            </div>
            <form onSubmit={handleDeleteAccount} className="space-y-4 px-6 py-6">
              <p className="text-sm text-slate-600">
                This action cannot be undone. Type <strong>DELETE</strong> below and enter your password to permanently delete your account.
              </p>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Type DELETE to confirm</span>
                <input
                  type="text"
                  className={inputClasses}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className={labelClasses}>Your password</span>
                <input
                  type="password"
                  className={inputClasses}
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                  className={secondaryButtonClasses}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deleteLoading || deleteConfirmText !== DELETE_CONFIRM_TEXT || !deletePassword.trim()}
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-red-600 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? "Deleting…" : "Delete my account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOverviewNew;
